/* eslint-disable arrow-parens */
import { useState, useCallback, useEffect, useContext, useRef, Suspense } from "react";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { api } from "../../../../../utils/api";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { url, port } from "../../../../../config/config";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import initCapitalize from "../../../../../utils/commonFunctions/initCapitalize";
import { AuthContext, GroupContext, UIContext } from "../../../../../contexts";
import CustomPagination from "../../../../shared/customPagination/CustomPagination1";
import getAgGridHeight from "../../../../../utils/commonFunctions/getAgGridHeight";

import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";

import VisibilityIcon from "@material-ui/icons/Visibility";
import DeleteIcon from "@material-ui/icons/Delete";
import CancelIcon from "@material-ui/icons/Cancel";
import { trackPromise } from "react-promise-tracker";
import { handleDateCellRenderer } from "../../../../../utils/commonFunctions/cellRenderers";
import { Button, IconButton, Tooltip, Checkbox } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import { dateTimeZoneConverter } from "../../../../../utils/commonFunctions/date";
import ROLES from "../../../../../constants/roles";
import { LazyCustomDialog } from "../../../../shared";

const GreenCheckbox = withStyles({
	root: {
		color: green[400],
		"&$checked": {
			color: green[600]
		}
	},
	onClick: {},
	checked: {}
})((props) => <Checkbox color="default" {...props} size="small" />);

const VEBWaivedBenefitsGrid = () => {
	const [vebWaivedBenefitDetails, setVEBWaivedBenefitDetails] = useState([]);
	const [gridHeight, setGridHeight] = useState("400px");
	const [isReadyForExport, setIsReadyForExport] = useState(false);
	const [agGridAPI, setAgGridAPI] = useState(null);
	const [agGridColumnAPI, setAgGridColumnAPI] = useState(null);
	const { currentGroup, setCurrentGroup } = useContext(GroupContext);
	const [customDialogProps, setCustomDialogProps] = useState({
		openDialog: false,
		title: "",
		content: "",
		actions: []
	});
	const [pagination, setPagination] = useState({
		currentPage: 1,
		pageCount: 2
	});
	const agGridRef = useRef(null);
	const { setDashboardHeader } = useContext(UIContext);
	const { user } = useContext(AuthContext);
	const queryGroupNumber = useRouteParam("gn");

	const { currentPage, pageCount } = pagination;

	const handleAgGridReady = (params) => {
		const { api, columnApi } = params;
		setAgGridAPI(Object.assign({}, agGridAPI, api));
		setAgGridColumnAPI(columnApi);
	};

	const handleFirstDataRendered = (params) => {
		params.api.sizeColumnsToFit();
		params.columnApi.setColumnWidth("action", "100%", true);
		const { api, columnApi } = params;
		api.sizeColumnsToFit();
	};

	const handlePageChange = useCallback(
		(event, value) => {
			setPagination(Object.assign({}, pagination, { currentPage: value }));
		},
		[pagination]
	);

	const handleSSNCellRenderer = ({ value }) => {
		const SSN = value.substring(0, 3) + "-" + value.substring(3, 5) + "-" + value.substring(5);
		return <div>{SSN}</div>;
	};

	const handleActionCellRender = useCallback(() => {
		return (
			<>
				<Tooltip title="View" leaveDelay={200} enterDelay={500} arrow>
					<IconButton>
						<VisibilityIcon />
					</IconButton>
				</Tooltip>
				<Tooltip title="Clear" leaveDelay={200} enterDelay={500} arrow>
					<IconButton>
						<CancelIcon />
					</IconButton>
				</Tooltip>
			</>
		);
	}, []);

	const handleCloseCustomDialogClick = useCallback(() => {
		setCustomDialogProps(
			Object.assign({}, customDialogProps, {
				openDialog: false
			})
		);
	}, [customDialogProps]);

	const getVEBWaivedBenefitDetails = useCallback(async (queryGroupNumber) => {
		const _VEBWaivedBenefitDetails = [];
		const company = "veb";
		const response = await trackPromise(api.enrollments.getMemberWaivedBenefits(company, queryGroupNumber));
		if (response) {
			response.forEach((eachVEBWaivedBenefit) => {
				const {
					_id,
					plan_name,
					plan_code,
					employee,
					enrollment_status,
					group_number,
					location_number,
					enrollment_date,
					action_taken_against_waive,
					insured_SSN
				} = eachVEBWaivedBenefit;

				_VEBWaivedBenefitDetails.push({
					_id,
					plan_name,
					plan_code,
					employee_name: employee.first_name + " " + employee.last_name,
					location_number,
					member_id: employee.member_id,
					insured_SSN,
					enrollment_status: initCapitalize(enrollment_status),
					enrollment_date,
					group_number,
					action_taken_against_waive
				});
			});
		}

		setVEBWaivedBenefitDetails(Object.assign([], _VEBWaivedBenefitDetails));
	}, []);

	const checkedActionAgainstWaiveClick = useCallback(
		async (event) => {
			// console.log(event.target.checked);
			const { checked } = event.target;
			const focusedCell = agGridRef.current.api.getFocusedCell();
			const row = agGridRef.current.api.getDisplayedRowAtIndex(focusedCell.rowIndex);
			const { data } = row;
			const { _id, group_number, action_taken_against_waive } = data;
			const company = "veb";
			const waivedBenefitPayload = {
				waivedActionValue: checked
			};
			if (!action_taken_against_waive) {
				setCustomDialogProps(
					Object.assign({}, customDialogProps, {
						openDialog: true,
						title: "Do you want to remove this VEB waived benefit ?",
						content: "Click Yes to proceed, otherwise Cancel",
						actions: [
							{
								label: "Cancel",
								name: "cancel",
								callback: () => {
									handleCloseCustomDialogClick();
								}
							},
							{
								label: "Yes",
								name: "submit",
								callback: async () => {
									const response = await trackPromise(
										api.enrollments.editActionTakenAgainstWaive(
											_id,
											company,
											group_number,
											waivedBenefitPayload
										)
									);
									if (response) {
										getVEBWaivedBenefitDetails(group_number);
									}

									handleCloseCustomDialogClick();
								}
							}
						]
					})
				);
			} else {
				alert("Action has already been taken for this VEB waived benefit!");
			}
		},
		[customDialogProps, getVEBWaivedBenefitDetails, handleCloseCustomDialogClick]
	);

	const handleActionAgainstWaiveCheckCellRender = useCallback(
		(value) => {
			return (
				<GreenCheckbox
					onClick={(event) => checkedActionAgainstWaiveClick(event)}
					checked={value.data.action_taken_against_waive}
				/>
			);
		},
		[checkedActionAgainstWaiveClick]
	);

	useEffect(() => {
		if (queryGroupNumber) getVEBWaivedBenefitDetails(queryGroupNumber);
	}, [getVEBWaivedBenefitDetails, queryGroupNumber]);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.employer_VEB_waived_benefit);
	}, [setDashboardHeader]);

	useEffect(() => {
		const _gridHeight = getAgGridHeight(vebWaivedBenefitDetails.length);
		setGridHeight(_gridHeight);
	}, [vebWaivedBenefitDetails.length]);

	const handleExportButtonClick = useCallback(() => {
		agGridRef?.current?.api.exportDataAsCsv({
			fileName: "Enrollment Approvals.csv",
			processCellCallback: (params) => {
				// console.log("params", params);
				switch (params.column.colId) {
					case "employee": {
						return params.value.first_name + " " + params.value.last_name;
					}
					case "enrollment_date": {
						return dateTimeZoneConverter(params.value);
					}
					default:
						return params.value;
				}
			}
		});
	}, []);

	useEffect(() => {
		if (vebWaivedBenefitDetails.length !== 0) {
			setIsReadyForExport(true);
		}
	}, [vebWaivedBenefitDetails.length]);

	useEffect(() => {
		if (queryGroupNumber) {
			const fetchGroup = async () => {
				try {
					const group = await api.group.getGroupByGroupNumber(queryGroupNumber);
					setCurrentGroup(group);
				} catch (error) {
					console.error("Error fetching group:", error);
				}
			};

			fetchGroup();
		}
	}, [queryGroupNumber, setCurrentGroup]);

	return (
		<>
			<Suspense fallback={<div />}>
				<LazyCustomDialog dialogProps={customDialogProps} handleClose={handleCloseCustomDialogClick} />
			</Suspense>
			<div className="waived-benefit-grid-container glb-tab-grid-container" id="waived-benefit-grid-container">
				<div className="ag-theme-alpine" style={{ height: gridHeight, width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={vebWaivedBenefitDetails}
						onGridReady={handleAgGridReady}
						onFirstDataRendered={handleFirstDataRendered}
						rowSelection={"Single"}
						animateRows
						suppressColumnVirtualisation={true}
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true,
							floatingFilterComponentParams: { suppressFilterButton: true }
						}}
						frameworkComponents={{
							handleActionCellRender: handleActionCellRender,
							handleSSNCellRenderer: handleSSNCellRenderer,
							handleDateCellRenderer: handleDateCellRenderer,
							handleActionAgainstWaiveCheckCellRender: handleActionAgainstWaiveCheckCellRender
						}}
					>
						<AgGridColumn
							field="action_taken_against_waive"
							headerName="Action Taken Against Waive"
							cellRenderer="handleActionAgainstWaiveCheckCellRender"
							filter={false}
							suppressSizeToFit
						></AgGridColumn>
						{user.role === ROLES.super_admin &&
						((user.first_name === "Chitrak" && user.last_name === "Biswas") ||
							(user.first_name === "Anupam" && user.last_name === "Biswas") ||
							(user.first_name === "Rahul" && user.last_name === "Dutta") ||
							(user.first_name === "Suman" && user.last_name === "Dutta")) ? (
							<AgGridColumn
								field="group_number"
								headerName="Group Number"
								floatingFilterComponentParams={{ suppressFilterButton: true }}
								suppressSizeToFit
							></AgGridColumn>
						) : null}
						<AgGridColumn
							field="location_number"
							headerName="Location No."
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="member_id"
							headerName="Employee Id"
							suppressSizeToFit
							floatingFilterComponentParams={{ suppressFilterButton: true }}
						></AgGridColumn>
						<AgGridColumn
							field="employee_name"
							headerName="Employee Name"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="insured_SSN"
							// headerName="ALT-ID"
							headerName={currentGroup?.SSN_provided_by_group ? "SSN" : "ALT-ID"}
							cellRenderer="handleSSNCellRenderer"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="plan_code"
							headerName="Plan Code"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="plan_name"
							headerName="Plan Name"
							suppressSizeToFit
							floatingFilterComponentParams={{ suppressFilterButton: true }}
						></AgGridColumn>
						<AgGridColumn
							field="enrollment_date"
							headerName="Waived Date"
							suppressSizeToFit
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							cellRenderer="handleDateCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							field="enrollment_status"
							headerName="Status"
							filter={false}
							suppressSizeToFit
						></AgGridColumn>
						{/* <AgGridColumn
							field="action_taken_against_waive"
							headerName="Action Taken Against Waive"
							cellRenderer="handleActionAgainstWaiveCheckCellRender"
							floatingFilterComponentParams={{ suppressFilterButton: true }}
							suppressSizeToFit
						></AgGridColumn>
						{user.role === ROLES.super_admin &&
						((user.first_name === "Chitrak" && user.last_name === "Biswas") ||
							(user.first_name === "Anupam" && user.last_name === "Biswas") ||
							(user.first_name === "Rahul" && user.last_name === "Dutta") || (user.first_name === "Suman" && user.last_name === "Dutta")) ? (
							<AgGridColumn
								field="group_number"
								headerName="Group Number"
								suppressSizeToFit
							></AgGridColumn>
						) : null}
						<AgGridColumn
							field="employee_location"
							headerName="Location No."
							suppressSizeToFit={true}
						></AgGridColumn>

						<AgGridColumn field="employee_id" headerName="Employee Id" suppressSizeToFit></AgGridColumn>
						<AgGridColumn
							field="employee_name"
							headerName="Employee Name"
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn field="plan_code" headerName="Plan Code" suppressSizeToFit={true}></AgGridColumn>
						<AgGridColumn field="plan_name" headerName="Plan Name" suppressSizeToFit></AgGridColumn>
						<AgGridColumn
							field="enrollment_date"
							headerName="Waived Date"
							suppressSizeToFit
							cellRenderer="handleDateCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							field="enrollment_status"
							headerName="Status"
							filter={false}
							suppressSizeToFit
						></AgGridColumn> */}
						{/* <AgGridColumn
							field="action"
							headerName="Action"
							cellRenderer="handleActionCellRender"
							filter={false}
							suppressSizeToFit={true}
						></AgGridColumn> */}
					</AgGridReact>
				</div>
			</div>
			{/* <div className="custom-pagination" id="custom-pagination">
				<CustomPagination pageCount={pageCount} currentPage={currentPage} handlePageChange={handlePageChange} />
			</div> */}
			{isReadyForExport ? (
				<div className="pf-action-button-container mg-top-20 mg-right-ve10" id="pf-action-button-container">
					<Button className="button-green" variant="contained" onClick={handleExportButtonClick}>
						<span className="button-label-with-icon">Export</span>
						<span>
							<ImportExportIcon className="button-icon" />
						</span>
					</Button>
				</div>
			) : null}
		</>
	);
};

export default VEBWaivedBenefitsGrid;
